import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import { Label } from "@/modules/shared/ui/components/label"

interface BulkRejectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (reason: string) => Promise<void>
  selectedCount: number
  isSubmitting: boolean
}

export function BulkRejectDialog({
  open,
  onOpenChange,
  onConfirm,
  selectedCount,
  isSubmitting,
}: BulkRejectDialogProps) {
  const [reason, setReason] = useState("")

  const handleConfirm = async () => {
    await onConfirm(reason)
    setReason("")
  }

  const handleCancel = () => {
    setReason("")
    onOpenChange(false)
  }

  const label =
    selectedCount === 1 ? "1 Candidate" : `${selectedCount} Candidates`

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-card">
        <DialogHeader>
          <DialogTitle>Reject {label}</DialogTitle>
          <DialogDescription>
            A rejection email will be sent to each candidate. This action cannot
            be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bulk-rejection-reason">
              Rejection Reason{" "}
              <span className="text-muted-foreground font-normal">
                (optional)
              </span>
            </Label>
            <Textarea
              id="bulk-rejection-reason"
              placeholder="Enter the reason for rejection..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              disabled={isSubmitting}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              The same reason will be applied to all selected candidates.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting}
            variant="destructive"
          >
            {isSubmitting ? "Submitting..." : `Reject ${label}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
